import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { formatDate } from "../../api/dates";
import { formatMoney } from "../../api/money";
import { PAGE_SIZE, useRegisterPage, type Queue, type QuotationRow, type Stage } from "../../api/quotations";
import { Pill, StatusChip } from "../../components/ui";
import { useDebounced } from "../../hooks/useDebounced";
import { count } from "./JobsRail";
import { jobRef } from "./pipeline";
import { queueLabel, sectionsFor } from "./queues";
import { quotationLabel, quotationTone } from "./tones";

interface Props {
  selectedId: number | null;
  supplierView: boolean;
  onClose: () => void;
  onPick: (quotationId: number) => void;
}

/** Every job, full screen, fifty at a time: the register for when the rail's
 * queues are not the question -- looking a job up by its site, or reading
 * down a month of them. Filters, counts and pages are the server's (Fix #23);
 * Previous walks back through the cursors this overlay has already used. */
export default function AllJobs({ selectedId, supplierView, onClose, onPick }: Props) {
  const [text, setText] = useState("");
  const [stage, setStage] = useState<Stage | null>(null);
  const [cursors, setCursors] = useState<(number | null)[]>([null]);
  const search = useDebounced(text.trim(), 250);
  const cursor = cursors[cursors.length - 1];
  const page = useRegisterPage(stage, search, cursor);
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    heading.current?.focus();
  }, []);

  useEffect(() => {
    setCursors([null]);
  }, [search, stage]);

  const counts = page.data?.counts;
  const rows = page.data?.rows ?? [];
  const total = page.data?.total ?? 0;
  const start = (cursors.length - 1) * PAGE_SIZE;
  const filters: { value: Stage | null; label: string; n: number | undefined }[] = [
    { value: null, label: "All", n: counts?.all },
    ...sectionsFor(supplierView).map((section) => ({
      value: section.key as Queue,
      label: section.label,
      n: counts?.[section.key],
    })),
    { value: "done", label: "Done", n: counts?.done },
  ];

  function onKeyDown(event: KeyboardEvent<HTMLTableRowElement>, row: QuotationRow) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onPick(row.id);
    }
  }

  return (
    <div
      className="cm-all"
      role="dialog"
      aria-modal="true"
      aria-labelledby="all-jobs-title"
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          onClose();
        }
      }}
    >
      <div className="cm-all-head">
        <h2 id="all-jobs-title" ref={heading} tabIndex={-1}>
          All jobs <span className="cm-rail-count">{count(counts?.all)}</span>
        </h2>
        <input
          type="search"
          className="search-input cm-all-search"
          placeholder="Search ref, site, supplier or works order"
          aria-label="Search all jobs"
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <button type="button" className="btn cm-all-close" onClick={onClose}>
          {selectedId !== null ? "Back to the job" : "Close"}
        </button>
        <div className="pill-row cm-all-filters" role="group" aria-label="Filter jobs">
          {filters.map((filter) => (
            <Pill key={filter.value ?? "all"} on={stage === filter.value} onToggle={() => setStage(filter.value)} count={count(filter.n)}>
              {filter.label}
            </Pill>
          ))}
        </div>
      </div>

      <div className="table-card cm-all-table">
        {page.isError ? <p className="notice bad">{page.error.message}</p> : null}
        <table className="data-table" aria-label="All jobs">
          <thead>
            <tr>
              <th>Ref</th>
              <th>Site</th>
              {supplierView ? null : <th>Supplier</th>}
              <th>Status</th>
              <th>Where it is</th>
              <th>Dated</th>
              <th className="num">Excl VAT</th>
            </tr>
          </thead>
          <tbody>
            {page.isPending ? (
              <tr>
                <td colSpan={7} className="muted">
                  Loading…
                </td>
              </tr>
            ) : null}
            {page.isSuccess && rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="muted">
                  Nothing matches. Clear the search or pick another filter.
                </td>
              </tr>
            ) : null}
            {rows.map((row) => (
              <tr
                key={row.id}
                className={row.id === selectedId ? "clickable selected" : "clickable"}
                tabIndex={0}
                onClick={() => onPick(row.id)}
                onKeyDown={(event) => onKeyDown(event, row)}
                data-testid={`all-jobs-row-${row.id}`}
              >
                <td className="mono">{jobRef(row.supplier, row.number)}</td>
                <td>{row.site?.name ?? <span className="muted">not linked</span>}</td>
                {supplierView ? null : <td className="muted">{row.supplier?.name ?? "—"}</td>}
                <td>
                  <StatusChip tone={quotationTone(row.status)}>{quotationLabel(row.status)}</StatusChip>
                </td>
                <td>
                  <span className={`status-dot cm-queue-dot q-${row.queue}`}>{queueLabel(row.queue, supplierView)}</span>
                </td>
                <td className="muted">{formatDate(row.quote_date)}</td>
                <td className="num">{formatMoney(row.total_excl_vat)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pager">
        <span className="pager-text">
          {total === 0 ? "No jobs" : `${count(start + 1)}–${count(start + rows.length)} of ${count(total)}`}
        </span>
        <button
          type="button"
          className="btn"
          disabled={cursors.length === 1}
          onClick={() => setCursors((now) => now.slice(0, -1))}
        >
          ‹ Previous
        </button>
        <button
          type="button"
          className="btn"
          disabled={page.data?.next_cursor == null || page.isPlaceholderData}
          onClick={() => {
            const next = page.data?.next_cursor;
            if (next !== undefined && next !== null) {
              setCursors((now) => [...now, next]);
            }
          }}
        >
          Next ›
        </button>
      </div>
    </div>
  );
}
